import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { blogPosts, getBlogPostBodyBySlug, getBlogPostBySlug } from '../data/blogData';
import { getSeedComments } from '../data/blogComments';
import {
  getBlogCategory,
  getBlogTrustNotice,
  normalizeBlogHeadings,
} from '../data/blogSeo';
import { ROUTES, SERVICE_ROUTES } from '../data/siteLinks';
import NotFoundPage from './NotFoundPage';

// Yorum bölümü şimdilik kapalı (Patron kararı 2026-06-07). Geri açmak için true yap.
const COMMENTS_ENABLED = false;

// Blog post slug → YouTube video URL eşlemesi. Bu eşlemede olan post'larda
// başlığın altında "Bu yazının videosu da var" rozeti gösterilir.
// Slug → tek video URL veya 2+ video URL listesi
const POST_VIDEO_URLS: Record<string, string | string[]> = {
  '/post/telc-b1-hazirlik-sureci': 'https://www.youtube.com/watch?v=vyi3hSKCRvA',
  '/post/telc-b1-sinav-gunu': 'https://youtu.be/HTAllXXePIY',
  '/post/almanyaya-nasil-goc-ederim': 'https://youtu.be/P_WqJNO0b_4',
  '/post/almanyaya-goc-etmenin-yollari': 'https://youtu.be/UZ6vNlcTQac',
  '/post/almanyada-ehliyet-sureci': 'https://youtu.be/R9cdkzPKCZA',
  '/post/blue-card-bilgi': 'https://youtu.be/x6cRdwLdb6U',
  '/post/almanya-blue-card-vize': 'https://youtu.be/1CcKRRjy_wk',
  '/post/almanyada-pratik-bilgiler': 'https://youtu.be/RXg5YofnlCM',
  '/post/almanyaya-geldik-simdi-ne-olacak': 'https://youtu.be/GYQaJwHkXXw',
  '/post/kalici-oturum-a1-einburgerungstest': 'https://www.youtube.com/watch?v=ePSuVUNQm_U',
  '/post/almanyada-is-bulmak': 'https://www.youtube.com/watch?v=XM8uoks1W_k',
  '/post/almanyada-yasam': 'https://www.youtube.com/watch?v=GXHUmxXmQg8',
  '/post/almanyada-sirket-kurmak': 'https://www.youtube.com/watch?v=9IG6hk1oEJk',
  '/post/almanya-da-kredi-cekmek': 'https://www.youtube.com/watch?v=Z-aLILCIxSw',
  '/post/almanyada-yasam-guven': 'https://www.youtube.com/watch?v=31pSF6b53uM',
  '/post/koln-munih': 'https://www.youtube.com/watch?v=_7wPEfUBGd4',
  '/post/almanya_ezb_formu': 'https://www.youtube.com/watch?v=PqIOcy9sw1s',
  '/post/ab-vatandaslarinin-almanyaya-gocu': 'https://www.youtube.com/watch?v=-p2Q_5dVvUw',
  '/post/almanyada-kariyer-degisimi': 'https://www.youtube.com/watch?v=fcU6cDcwEtw',
  '/post/almanyada-okula-kayit-sureci': [
    'https://www.youtube.com/watch?v=vCoxob05gHg',
    'https://www.youtube.com/watch?v=kLx9BmSBHoM',
  ],
};

const ALL_TAB = 'Tüm Yazılar';
const categoryOrder = [
  ALL_TAB,
  'Almanya Blue Card',
  "Almanya'da Eğitim",
  "Almanya'da Yaşam",
  "Ayşegül'den",
  "Almanya'ya Göç",
];

// Bu yazılarda hero görsel gösterilmeyecek (kart thumbnail'i etkilenmez,
// yalnızca yazı detay sayfasındaki üst görsel gizlenir).
const HIDE_HERO_SLUGS = new Set<string>([
  '/post/almanyada-is-bulmak',
  '/post/almanyada-yasam',
  '/post/almanyada-yasam-guven',
  '/post/almanya_ezb_formu',
  '/post/almanyaya_goc',
  '/post/ab-vatandaslarinin-almanyaya-gocu',
  '/post/almanyaya-nasil-goc-ederim',
  '/post/almanyada-kariyer-degisimi',
  '/post/almanyada-anmeldung-nedir-ve-nasil-yapilir',
  '/post/almanyada-devlet-destekli-dil-kurslari',
]);

type ServiceTransition = {
  eyebrow: string;
  title: string;
  text: string;
  linkLabel: string;
  path: string;
  whatsappMessage?: string;
};

const createDraftTransition = (
  title: string,
  text: string,
  path: string,
  whatsappMessage: string,
): ServiceTransition => ({
  eyebrow: 'İlgili rehberlik hizmeti',
  title,
  text,
  linkLabel: 'Hizmeti İnceleyin',
  path,
  whatsappMessage,
});

const SCHEDULED_SERVICE_TRANSITIONS: Record<string, ServiceTransition> = {
  '/post/almanyada-mektuplari-duzenleme': createDraftTransition(
    'Almanya’daki ilk işlemlerinizi daha düzenli takip edin',
    'Yerleşim sonrasında gelen yazıları, tarihleri ve günlük işlemleri planlamaya yönelik tecrübeye dayalı göç sonrası rehberlik hizmetini inceleyebilirsiniz.',
    SERVICE_ROUTES.postMigration,
    'Merhaba, Almanya’da mektupları düzenleme yazınızı okudum. Göç sonrası rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  ),
  '/post/almanyada-toplu-tasima-uygulamalari': createDraftTransition(
    'Almanya’daki ilk döneminizi daha planlı geçirin',
    'Günlük yaşam, ulaşım ve Almanya’ya yerleştikten sonraki pratik adımlar için sunduğumuz tecrübeye dayalı destek hizmetini inceleyebilirsiniz.',
    SERVICE_ROUTES.postMigration,
    'Merhaba, Almanya’da toplu taşıma yazınızı okudum. Almanya’ya ilk gelenler için destek hizmetiniz hakkında bilgi almak istiyorum.',
  ),
  '/post/kita-cantasinda-neler-olmali': createDraftTransition(
    'Kita başlangıcınızı daha planlı hazırlayın',
    'Kita ve Kindergarten sürecindeki kayıt öncesi hazırlıklar ile günlük düzen için sunduğumuz deneyime dayalı eğitim rehberlik hizmetini inceleyebilirsiniz.',
    SERVICE_ROUTES.education,
    'Merhaba, Kita çantası yazınızı okudum. Eğitim rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  ),
  '/post/almanyada-kita-alisma-sureci-eingewohnung': createDraftTransition(
    'Kindergarten başlangıcınızı daha planlı hazırlayın',
    'Kurum araştırmasından ilk gün hazırlığına kadar eğitim sürecindeki organizasyonel adımlar için sunduğumuz deneyime dayalı rehberlik hizmetini inceleyebilirsiniz.',
    SERVICE_ROUTES.education,
    'Merhaba, Eingewöhnung yazınızı okudum. Eğitim rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  ),
};

// Search Console'da en çok ziyaret alan bilgilendirici yazılardan, yazının
// konusuyla doğrudan ilişkili hizmet sayfasına sade bir geçiş sağlar.
const POST_SERVICE_TRANSITIONS: Record<string, ServiceTransition> = {
  '/post/almanyada-schultüte': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Göç sonrasındaki okul başlangıcı hazırlıklarını planlayın',
    text: 'Almanya’daki okul hayatına başlangıç ve günlük yaşama uyum konularında sunduğumuz tecrübeye dayalı göç sonrası rehberlik hizmetini inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.postMigration,
    whatsappMessage: 'Merhaba, Schultüte yazınızı okudum. Göç sonrası rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/almanyada-kita-nasil-bulunur': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Kindergarten arayışınızı daha planlı yürütün',
    text: 'Almanya’da Kindergarten, Kita ve Tagesmutter seçeneklerini araştırma ve kayıt öncesindeki organizasyonel hazırlıklar için sunduğumuz deneyime dayalı rehberlik hizmetimizin kapsamını inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.education,
    whatsappMessage: 'Merhaba, Almanya’da Kita bulma yazınızı okudum. Kindergarten rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/almanyada-hausarzt-bulma': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Almanya’daki ilk döneminiz için pratik hazırlık desteği',
    text: 'Sağlık sistemi ve günlük yaşama ilişkin organizasyonel adımların da yer aldığı göç sonrası rehberlik hizmetimizin kapsamını görebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.postMigration,
    whatsappMessage: 'Merhaba, Hausarzt yazınızı okudum. Göç sonrası rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/almanyada-ehliyet-sureci': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Göç sonrasındaki pratik adımları planlayın',
    text: 'Almanya’daki günlük yaşam ve resmî işlemlere hazırlık konusunda sunduğumuz deneyime dayalı rehberlik hizmetini inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.postMigration,
    whatsappMessage: 'Merhaba, Almanya’da ehliyet süreci yazınızı okudum. Göç sonrası rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/almanya_ezb_formu': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Oturum sürecindeki hazırlık adımlarını inceleyin',
    text: 'Oturum işlemleri öncesinde belgelerin ve organizasyonel adımların planlanmasına yönelik tecrübeye dayalı rehberlik hizmetimizin kapsamını görebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.residence,
    whatsappMessage: 'Merhaba, EZB formu yazınızı okudum. Oturum rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/telc-b1-hazirlik-sureci': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Dil eğitimi seçeneklerinizi planlıyor musunuz?',
    text: 'Dil okulu araştırması ve kayıt öncesi hazırlık adımlarına ilişkin deneyime dayalı rehberlik hizmetimizi inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.education,
    whatsappMessage: 'Merhaba, Telc B1 hazırlık süreci yazınızı okudum. Dil okulu rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/telc-b1-sinav-gunu': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Dil eğitimi seçeneklerinizi planlıyor musunuz?',
    text: 'Dil okulu araştırması ve kayıt öncesi hazırlık adımlarına ilişkin deneyime dayalı rehberlik hizmetimizi inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.education,
    whatsappMessage: 'Merhaba, Telc B1 sınav günü yazınızı okudum. Dil okulu rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/kalici-oturum-a1-einburgerungstest': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Oturum sürecindeki hazırlık adımlarını inceleyin',
    text: 'Oturum işlemleri öncesinde belgelerin ve organizasyonel adımların planlanmasına yönelik tecrübeye dayalı rehberlik hizmetimizin kapsamını görebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.residence,
    whatsappMessage: 'Merhaba, kalıcı oturum, A1 Almanca ve Einbürgerungstest yazınızı okudum. Oturum rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/ab-vatandaslarinin-almanyaya-gocu': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Oturum sürecindeki hazırlık adımlarını inceleyin',
    text: 'Oturum işlemleri öncesinde belgelerin ve organizasyonel adımların planlanmasına yönelik tecrübeye dayalı rehberlik hizmetimizin kapsamını görebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.residence,
    whatsappMessage: 'Merhaba, AB vatandaşı eşiyle Almanya’ya göç yazınızı okudum. Oturum rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/almanyaya-nasil-goc-ederim': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Almanya’ya taşınma hazırlıklarınızı planlayın',
    text: 'Göç öncesindeki idari ve organizasyonel hazırlıkları daha planlı yürütmenize yardımcı olan deneyime dayalı süreç rehberliği hizmetimizi inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.migrationPreparation,
    whatsappMessage: 'Merhaba, Almanya’ya nasıl göç edilir yazınızı okudum. Süreç rehberliği hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/almanyaya-goc-etmenin-yollari': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Almanya’ya taşınma hazırlıklarınızı planlayın',
    text: 'Göç öncesindeki idari ve organizasyonel hazırlıkları daha planlı yürütmenize yardımcı olan deneyime dayalı süreç rehberliği hizmetimizi inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.migrationPreparation,
    whatsappMessage: 'Merhaba, Almanya’ya göç etmenin yolları yazınızı okudum. Süreç rehberliği hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/almanyaya-cocuk-ile-goc': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Almanya’ya taşınma hazırlıklarınızı planlayın',
    text: 'Çocukla göç öncesindeki idari ve organizasyonel hazırlıkları daha planlı yürütmenize yardımcı olan deneyime dayalı süreç rehberliği hizmetimizi inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.migrationPreparation,
    whatsappMessage: 'Merhaba, çocukla Almanya’ya göç yazınızı okudum. Süreç rehberliği hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/almanyaya-gidis': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Almanya’ya taşınma hazırlıklarınızı planlayın',
    text: 'Göç öncesindeki idari ve organizasyonel hazırlıkları daha planlı yürütmenize yardımcı olan deneyime dayalı süreç rehberliği hizmetimizi inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.migrationPreparation,
    whatsappMessage: 'Merhaba, Almanya’ya gidiş hazırlığı yazınızı okudum. Süreç rehberliği hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/koln-munih': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Almanya’ya taşınma hazırlıklarınızı planlayın',
    text: 'Taşınma öncesindeki seçenekleri ve organizasyonel hazırlıkları daha planlı değerlendirmenize yardımcı olan deneyime dayalı süreç rehberliği hizmetimizi inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.migrationPreparation,
    whatsappMessage: 'Merhaba, Köln mü Münih mi yazınızı okudum. Süreç rehberliği hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/blue-card-bilgi': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Vize başvurusu öncesi hazırlıklarınızı planlayın',
    text: 'Başvuru öncesindeki belgelerin ve organizasyonel adımların planlanmasına yönelik deneyime dayalı vize rehberlik hizmetimizin kapsamını inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.visa,
    whatsappMessage: 'Merhaba, Blue Card hakkında merak edilen sorular yazınızı okudum. Vize rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/almanya-blue-card-vize': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Vize başvurusu öncesi hazırlıklarınızı planlayın',
    text: 'Başvuru öncesindeki belgelerin ve organizasyonel adımların planlanmasına yönelik deneyime dayalı vize rehberlik hizmetimizin kapsamını inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.visa,
    whatsappMessage: 'Merhaba, Almanya Blue Card vizesi yazınızı okudum. Vize rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/almanya-blue-card': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Vize başvurusu öncesi hazırlıklarınızı planlayın',
    text: 'Başvuru öncesindeki belgelerin ve organizasyonel adımların planlanmasına yönelik deneyime dayalı vize rehberlik hizmetimizin kapsamını inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.visa,
    whatsappMessage: 'Merhaba, Blue Card nasıl alınır yazınızı okudum. Vize rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/almanyada-okula-kayit-sureci': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Okul kayıt hazırlıklarını daha planlı yürütün',
    text: 'Okul seçenekleri, kayıt öncesi hazırlık ve gerekli organizasyonel adımlara ilişkin deneyime dayalı eğitim rehberlik hizmetimizi inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.education,
    whatsappMessage: 'Merhaba, Almanya’da okul kayıt süreci yazınızı okudum. Eğitim rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/almanyada-anmeldung-nedir-ve-nasil-yapilir': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Almanya’daki ilk resmî işlemlerinizi planlayın',
    text: 'Anmeldung ve yerleşim sonrasındaki diğer organizasyonel adımlara ilişkin deneyime dayalı göç sonrası rehberlik hizmetimizi inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.postMigration,
    whatsappMessage: 'Merhaba, Anmeldung yazınızı okudum. Göç sonrası rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/almanyaya-geldik-simdi-ne-olacak': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Almanya’daki ilk işlemlerinizi daha planlı yürütün',
    text: 'Anmeldung, banka hesabı ve sağlık sigortası gibi ilk organizasyonel adımlara ilişkin deneyime dayalı göç sonrası rehberlik hizmetimizi inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.postMigration,
    whatsappMessage: 'Merhaba, Almanya’ya gelince yapılması gerekenler yazınızı okudum. Göç sonrası rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/almanyada-devlet-destekli-dil-kurslari': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Almanya’daki yeni hayatınıza daha hazırlıklı başlayın',
    text: 'Entegrasyon kursları ve uyum sürecindeki organizasyonel adımlara ilişkin deneyime dayalı entegrasyon rehberlik hizmetimizi inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.integration,
    whatsappMessage: 'Merhaba, devlet destekli dil kursları yazınızı okudum. Entegrasyon rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/almanyada-pratik-bilgiler': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Almanya’daki yeni hayatınıza daha hazırlıklı başlayın',
    text: 'Günlük yaşam, kültürel uyum ve Almanya’daki ilk döneme ilişkin deneyime dayalı entegrasyon rehberlik hizmetimizin kapsamını inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.integration,
    whatsappMessage: 'Merhaba, Almanya’ya gelenler için pratik bilgiler yazınızı okudum. Entegrasyon rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/almanyada-yasam': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Almanya’daki yeni hayatınıza daha hazırlıklı başlayın',
    text: 'Yeni yaşam düzeni ve kültürel uyum sürecinde deneyimlerimizden yararlanmak isterseniz entegrasyon rehberlik hizmetimizin kapsamını inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.integration,
    whatsappMessage: 'Merhaba, Almanya’da yaşam ve sabırlı olmayı öğrenmek yazınızı okudum. Entegrasyon rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/almanyada-yasam-guven': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Almanya’daki yeni hayatınıza daha hazırlıklı başlayın',
    text: 'Yeni yaşam düzeni ve kültürel uyum sürecinde deneyimlerimizden yararlanmak isterseniz entegrasyon rehberlik hizmetimizin kapsamını inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.integration,
    whatsappMessage: 'Merhaba, Almanya’da yaşam ve güven sistemi yazınızı okudum. Entegrasyon rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/goc-ettikten-sonra-degisen-aliskanliklar': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Almanya’daki yeni hayatınıza daha hazırlıklı başlayın',
    text: 'Yeni yaşam düzeni ve kültürel uyum sürecinde deneyimlerimizden yararlanmak isterseniz entegrasyon rehberlik hizmetimizin kapsamını inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.integration,
    whatsappMessage: 'Merhaba, göç ettikten sonra değişen alışkanlıklar yazınızı okudum. Entegrasyon rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/almanyaya_goc': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Almanya’daki yeni hayatınıza daha hazırlıklı başlayın',
    text: 'Yeni yaşam düzeni ve kültürel uyum sürecinde deneyimlerimizden yararlanmak isterseniz entegrasyon rehberlik hizmetimizin kapsamını inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.integration,
    whatsappMessage: 'Merhaba, göçün ardından hayatımızda neler değişti yazınızı okudum. Entegrasyon rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
  '/post/yil-donumu-hikayesi': {
    eyebrow: 'İlgili rehberlik hizmeti',
    title: 'Almanya’daki yeni hayatınıza daha hazırlıklı başlayın',
    text: 'Yeni yaşam düzeni ve kültürel uyum sürecinde deneyimlerimizden yararlanmak isterseniz entegrasyon rehberlik hizmetimizin kapsamını inceleyebilirsiniz.',
    linkLabel: 'Hizmeti İnceleyin',
    path: SERVICE_ROUTES.integration,
    whatsappMessage: 'Merhaba, Almanya’da bir yıl dönümü hikayesi yazınızı okudum. Entegrasyon rehberlik hizmetiniz hakkında bilgi almak istiyorum.',
  },
};

// Yayın tarihine göre skala edilir (yeni yazı < 15, eski yazı 1500'e kadar)
const fakeViewCount = (slug: string, publishedAt: string): number => {
  let hash = 0;
  for (let i = 0; i < slug.length; i++) {
    hash = (hash * 31 + slug.charCodeAt(i)) | 0;
  }
  const positive = Math.abs(hash);
  const days = Math.max(
    0,
    (Date.now() - new Date(publishedAt).getTime()) / 86400000,
  );

  let base: number;
  let variance: number;
  if (days < 1) {
    base = 3;
    variance = positive % 12;
  } else if (days < 7) {
    base = 15 + Math.floor(days * 6);
    variance = positive % 25;
  } else if (days < 30) {
    base = 60 + Math.floor((days - 7) * 8);
    variance = positive % 60;
  } else if (days < 90) {
    base = 260 + Math.floor((days - 30) * 5);
    variance = positive % 120;
  } else if (days < 365) {
    base = 600 + Math.floor((days - 90) * 2);
    variance = positive % 200;
  } else {
    base = 1100 + Math.floor((days - 365) * 0.5);
    variance = positive % 300;
  }

  return Math.min(1500, base + variance);
};

const VIEW_COUNT_OVERRIDES: Record<string, number> = {
  '/post/almanyada-kita-nasil-bulunur': 72,
};

const formatViews = (n: number) => n.toLocaleString('tr-TR');

const formatUpdatedDate = (iso: string) =>
  new Date(iso).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

const WHATSAPP_NUMBER = '4915228301212';
const buildWhatsAppLink = (postTitle: string) => {
  const message = `Merhaba, "${postTitle}" yazısı ile ilgili size bir sorum var.`;
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
};

const buildServiceWhatsAppLink = (message: string) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;

const addOfficialSourcesToInlineNotice = (
  html: string,
  sources: Array<{ label: string; url: string }>,
) => {
  const noticeColorIndex = html.lastIndexOf('background:#fff7e6');
  if (noticeColorIndex === -1) return html;

  const noticeStart = html.lastIndexOf('<div', noticeColorIndex);
  const noticeEnd = html.indexOf('</div>', noticeColorIndex);
  if (noticeStart === -1 || noticeEnd === -1) return html;

  const sourcesHtml = `<div class="blog-inline-official-sources"><strong>Güncel resmî kaynaklar:</strong><ul>${sources
    .map(
      (source) =>
        `<li><a href="${source.url}" target="_blank" rel="noopener noreferrer">${source.label}</a></li>`,
    )
    .join('')}</ul></div>`;

  return `${html.slice(0, noticeEnd)}${sourcesHtml}${html.slice(noticeEnd)}`;
};

const OPPORTUNITY_INTERNAL_LINKS: Record<
  string,
  Array<{ label: string; path: string }>
> = {
  '/post/almanya_ezb_formu': [
    { label: 'Almanya Blue Card nasıl alınır?', path: '/post/almanya-blue-card-vize' },
    { label: "Almanya'ya nasıl göç edilir?", path: '/post/almanyaya-nasil-goc-ederim' },
  ],
  '/post/almanyaya-nasil-goc-ederim': [
    { label: 'Almanya Blue Card nasıl alınır?', path: '/post/almanya-blue-card-vize' },
    { label: 'Zusatzblatt ve EZB formu', path: '/post/almanya_ezb_formu' },
  ],
  '/post/almanya-blue-card-vize': [
    { label: 'Zusatzblatt ve EZB formu', path: '/post/almanya_ezb_formu' },
    { label: "Almanya'ya göç etmenin temel yolları", path: '/post/almanyaya-nasil-goc-ederim' },
  ],
  '/post/almanyada-devlet-destekli-dil-kurslari': [
    { label: 'Entegrasyon Kursu Başvuru Rehberliği', path: '/hizmetler-entegrasyon' },
    { label: "Telc B1'e nasıl hazırlandım?", path: '/post/telc-b1-hazirlik-sureci' },
  ],
};

export const addOpportunityInternalLinks = (html: string, slug: string) => {
  const links = OPPORTUNITY_INTERNAL_LINKS[slug]?.filter(
    (item) => !html.includes(`href="${item.path}"`) && !html.includes(`href='${item.path}'`),
  );
  if (!links?.length) return html;

  const linksHtml = links
    .map((item) => `<li><a href="${item.path}">${item.label}</a></li>`)
    .join('');

  return `${html}<aside class="blog-opportunity-links" aria-label="İlgili içerikler"><strong>Bu konuyla ilgili:</strong><ul>${linksHtml}</ul></aside>`;
};

interface Comment {
  id: string;
  author: string;
  text: string;
  date: string; // ISO
}

const COMMENTS_KEY_PREFIX = 'aysegulsarac:comments:';
const LIKES_KEY_PREFIX = 'aysegulsarac:liked:';

const loadComments = (slug: string): Comment[] => {
  try {
    const raw = window.localStorage.getItem(COMMENTS_KEY_PREFIX + slug);
    if (!raw) return [];
    return JSON.parse(raw) as Comment[];
  } catch {
    return [];
  }
};
const saveComments = (slug: string, list: Comment[]) => {
  try {
    window.localStorage.setItem(COMMENTS_KEY_PREFIX + slug, JSON.stringify(list));
  } catch {
    // ignore quota / privacy mode
  }
};
const loadLiked = (slug: string): boolean => {
  try {
    return window.localStorage.getItem(LIKES_KEY_PREFIX + slug) === '1';
  } catch {
    return false;
  }
};
const saveLiked = (slug: string, value: boolean) => {
  try {
    if (value) window.localStorage.setItem(LIKES_KEY_PREFIX + slug, '1');
    else window.localStorage.removeItem(LIKES_KEY_PREFIX + slug);
  } catch {
    // ignore
  }
};

const formatCommentDate = (iso: string) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
};

const BlogPostPage: React.FC = () => {
  const params = useParams();
  const slug = params.slug ?? '';
  const post = getBlogPostBySlug(slug);
  const [bodyHtml, setBodyHtml] = useState<string>('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [liked, setLiked] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentText, setCommentText] = useState('');
  const [commentAuthor, setCommentAuthor] = useState('');

  useEffect(() => {
    if (!post) return;
    let active = true;
    getBlogPostBodyBySlug(slug).then((html) => {
      if (active) setBodyHtml(html || '');
    });
    return () => {
      active = false;
    };
  }, [post, slug]);

  const fullSlug = post?.slug.startsWith('/post/') ? post.slug : `/post/${post?.slug ?? slug}`;

  // Yorumları ve beğeni durumunu sayfaya girince yükle.
  // Önce slug'a özel önceden hazırlanmış seed yorumları, sonra kullanıcının
  // localStorage'a eklediklerini birleştir.
  useEffect(() => {
    if (!COMMENTS_ENABLED) return;
    if (!post) return;
    const cat = getBlogCategory(fullSlug);
    const seed = getSeedComments(fullSlug, post.title, cat);
    const userAdded = loadComments(fullSlug);
    setComments([...seed, ...userAdded]);
    setLiked(loadLiked(fullSlug));
  }, [post, fullSlug]);

  // Aynı kategoriden 3 ilgili yazı
  const relatedPosts = useMemo(() => {
    if (!post) return [];
    const cat = getBlogCategory(fullSlug);
    const now = Date.now();
    return blogPosts
      .filter((p) => {
        const ps = p.slug.startsWith('/post/') ? p.slug : `/post/${p.slug}`;
        return (
          ps !== fullSlug &&
          getBlogCategory(ps) === cat &&
          new Date(p.publishedAt).getTime() <= now
        );
      })
      .slice(0, 3);
  }, [post, fullSlug]);

  const trustNotice = getBlogTrustNotice(fullSlug);
  const normalizedBodyHtml = useMemo(() => {
    if (!post || !bodyHtml) return bodyHtml;

    let normalized = normalizeBlogHeadings(bodyHtml, post.title);
    if (trustNotice) {
      normalized = addOfficialSourcesToInlineNotice(normalized, trustNotice.sources);
    }

    return addOpportunityInternalLinks(normalized, fullSlug);
  }, [bodyHtml, fullSlug, post, trustNotice]);

  if (!post) {
    return <NotFoundPage />;
  }

  const shareUrl = `https://aysegulsarac.com${fullSlug}`;
  const category = getBlogCategory(fullSlug);
  const views = VIEW_COUNT_OVERRIDES[fullSlug] ?? fakeViewCount(fullSlug, post.publishedAt);
  const publishedDate = formatUpdatedDate(post.publishedAt);
  const updatedDate = formatUpdatedDate(post.updatedAt);
  const hasDistinctUpdatedDate = publishedDate !== updatedDate;
  const serviceTransition = POST_SERVICE_TRANSITIONS[fullSlug] || SCHEDULED_SERVICE_TRANSITIONS[fullSlug];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (e) {
      // ignore
    }
  };

  const toggleLike = () => {
    const next = !liked;
    setLiked(next);
    saveLiked(fullSlug, next);
  };

  const submitComment = (e: React.FormEvent) => {
    e.preventDefault();
    const text = commentText.trim();
    if (!text) return;
    const newComment: Comment = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      author: commentAuthor.trim() || 'Misafir',
      text,
      date: new Date().toISOString(),
    };
    // Sadece kullanıcı eklediklerini localStorage'a yaz; seed'ler kod tarafında.
    const userAdded = loadComments(fullSlug);
    saveComments(fullSlug, [...userAdded, newComment]);
    setComments([...comments, newComment]);
    setCommentText('');
    setCommentAuthor('');
  };

  return (
    <div className="blog-post-page">
      {/* Üst kategori şeridi (BlogPage ile aynı) */}
      <div className="container">
        <div className="blog-categories-row">
          {categoryOrder.map((cat) => (
            <Link
              key={cat}
              to={cat === ALL_TAB ? ROUTES.blog : `${ROUTES.blog}?kategori=${encodeURIComponent(cat)}`}
              className={`blog-category-tab blog-category-tab-link${cat === category ? ' active' : ''}`}
            >
              {cat}
            </Link>
          ))}
        </div>
      </div>

      <div className="container blog-post-container">
        <article className="blog-post-article">
          <header className="blog-post-header">
            <h1 className="blog-post-title">{post.title}</h1>
            <div className="blog-post-meta">
              <div className="blog-post-meta-primary">
                <span className="blog-post-author">
                  <svg
                    className="blog-post-author-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L8 18l-4 1 1-4Z" />
                  </svg>
                  {post.author}
                </span>
                <span className="blog-post-meta-dot" aria-hidden="true">·</span>
                <span>{post.readingTime}</span>
              </div>
              <div className="blog-post-meta-dates">
                <span>
                  Yayın tarihi: <time dateTime={post.publishedAt}>{publishedDate}</time>
                </span>
                {hasDistinctUpdatedDate && (
                  <span>
                    Son güncelleme: <time dateTime={post.updatedAt}>{updatedDate}</time>
                  </span>
                )}
              </div>
            </div>
            {(() => {
              const v = POST_VIDEO_URLS[fullSlug];
              if (!v) return null;
              const urls = Array.isArray(v) ? v : [v];
              return urls.map((url, idx) => (
                <a
                  key={url}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="blog-post-video-badge"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M21.6 7.2c-.2-.9-.9-1.6-1.8-1.8C18.2 5 12 5 12 5s-6.2 0-7.8.4c-.9.2-1.6.9-1.8 1.8C2 8.8 2 12 2 12s0 3.2.4 4.8c.2.9.9 1.6 1.8 1.8C5.8 19 12 19 12 19s6.2 0 7.8-.4c.9-.2 1.6-.9 1.8-1.8.4-1.6.4-4.8.4-4.8s0-3.2-.4-4.8zM10 15V9l5.2 3L10 15z" />
                  </svg>
                  <span>
                    {urls.length > 1
                      ? `Bu yazının ${idx + 1}. videosu — YouTube'da izle`
                      : "Bu yazının videosu da var — YouTube'da izle"}
                  </span>
                </a>
              ));
            })()}
            <p className="blog-post-excerpt">{post.excerpt}</p>
          </header>

          {post.image && !HIDE_HERO_SLUGS.has(fullSlug) && (
            <div className="blog-post-hero">
              <img src={post.image} alt={post.title} loading="eager" decoding="async" />
            </div>
          )}

          {normalizedBodyHtml ? (
            <div
              className="blog-post-body"
              dangerouslySetInnerHTML={{ __html: normalizedBodyHtml }}
            />
          ) : (
            <div className="blog-post-loading">Yazı yükleniyor...</div>
          )}

          {trustNotice && !bodyHtml?.includes('background:#fff7e6') && (
            <aside className="blog-post-trust-note" aria-labelledby="blog-trust-title">
              <h2 id="blog-trust-title">{trustNotice.title}</h2>
              <p>{trustNotice.text}</p>
              <p className="blog-post-trust-sources-label">Güncel resmî kaynaklar:</p>
              <ul>
                {trustNotice.sources.map((source) => (
                  <li key={source.url}>
                    <a href={source.url} target="_blank" rel="noopener noreferrer">
                      {source.label}
                    </a>
                  </li>
                ))}
              </ul>
            </aside>
          )}

          {serviceTransition && (
            <aside
              id="ilgili-rehberlik"
              className="blog-service-transition"
              aria-labelledby="blog-service-transition-title"
            >
              <p className="blog-service-transition-eyebrow">{serviceTransition.eyebrow}</p>
              <h2 id="blog-service-transition-title">{serviceTransition.title}</h2>
              <p>{serviceTransition.text}</p>
              <div className="blog-service-transition-actions">
                <Link className="blog-service-transition-link" to={serviceTransition.path}>
                  {serviceTransition.linkLabel}
                  <span aria-hidden="true">→</span>
                </Link>
                {serviceTransition.whatsappMessage && (
                  <a
                    className="blog-service-transition-link blog-service-transition-whatsapp"
                    href={buildServiceWhatsAppLink(serviceTransition.whatsappMessage)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M20.52 3.48A11.82 11.82 0 0 0 12.08 0C5.54 0 .22 5.32.22 11.86c0 2.09.55 4.13 1.58 5.92L.12 24l6.36-1.67a11.83 11.83 0 0 0 5.59 1.42h.01c6.54 0 11.86-5.32 11.86-11.86 0-3.17-1.21-6.15-3.42-8.41Zm-8.44 18.27h-.01a9.84 9.84 0 0 1-5.02-1.38l-.36-.21-3.77.99 1.01-3.68-.24-.38a9.83 9.83 0 0 1-1.51-5.23c0-5.45 4.44-9.89 9.9-9.89a9.82 9.82 0 0 1 7 2.9 9.82 9.82 0 0 1 2.89 7.01c0 5.45-4.44 9.87-9.87 9.87Zm5.42-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47a8.91 8.91 0 0 1-1.65-2.05c-.17-.3-.02-.46.13-.61.13-.13.3-.35.44-.52.15-.17.2-.3.3-.49.1-.2.05-.37-.02-.52-.08-.15-.67-1.61-.91-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.19 1.87.12.57-.08 1.76-.72 2.01-1.41.25-.69.25-1.29.17-1.41-.07-.12-.27-.2-.57-.35Z" />
                    </svg>
                    WhatsApp’tan bilgi alın
                  </a>
                )}
              </div>
            </aside>
          )}

          {/* Yazı altı: paylaşım + görüntülenme */}
          <footer className="blog-post-footer">
            {!serviceTransition && (
              <div className="blog-post-cta">
                <p className="blog-post-cta-text">
                  Bu yazı ile ilgili bana sormak istediğiniz bir şey varsa lütfen bana sorun.
                  Genellikle 24 saat içinde geri dönmeye çalışıyorum.
                </p>
                <a
                  href={buildWhatsAppLink(post.title)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="blog-post-cta-btn"
                >
                  Mesaj Gönderin
                </a>
              </div>
            )}

            <div className="blog-post-share-row">
              <span className="blog-post-share-title">Bu yazıyı paylaşın</span>
              <div className="blog-post-share-icons">
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="blog-post-share-btn"
                  aria-label="Facebook'ta paylaş"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.91h2.54V9.84c0-2.52 1.49-3.91 3.78-3.91 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.44 2.91h-2.34v7.03C18.34 21.21 22 17.06 22 12.06z" />
                  </svg>
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="blog-post-share-btn"
                  aria-label="X'te paylaş"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.215-6.817-5.969 6.817H1.674l7.731-8.835L1.25 2.25h6.83l4.713 6.231 5.451-6.231zm-1.16 17.52h1.834L7.084 4.126H5.117l11.967 15.644z" />
                  </svg>
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="blog-post-share-btn"
                  aria-label="LinkedIn'de paylaş"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14zM8.34 18.34V10.5H5.67v7.84h2.67zM7 9.32a1.55 1.55 0 1 0 0-3.1 1.55 1.55 0 0 0 0 3.1zm11.34 9.02v-4.3c0-2.48-1.32-3.63-3.09-3.63-1.43 0-2.07.79-2.43 1.34V10.5h-2.67c.04.75 0 7.84 0 7.84h2.67v-4.38c0-.24.02-.48.09-.65.19-.48.63-.97 1.36-.97.96 0 1.34.73 1.34 1.8v4.2h2.73z" />
                  </svg>
                </a>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`${post.title} ${shareUrl}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="blog-post-share-btn blog-post-share-whatsapp"
                  aria-label="WhatsApp'ta paylaş"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M20.52 3.48A11.82 11.82 0 0 0 12.08 0C5.54 0 .22 5.32.22 11.86c0 2.09.55 4.13 1.58 5.92L.12 24l6.36-1.67a11.83 11.83 0 0 0 5.59 1.42h.01c6.54 0 11.86-5.32 11.86-11.86 0-3.17-1.21-6.15-3.42-8.41Zm-8.44 18.27h-.01a9.84 9.84 0 0 1-5.02-1.38l-.36-.21-3.77.99 1.01-3.68-.24-.38a9.83 9.83 0 0 1-1.51-5.23c0-5.45 4.44-9.89 9.9-9.89a9.82 9.82 0 0 1 7 2.9 9.82 9.82 0 0 1 2.89 7.01c0 5.45-4.44 9.87-9.87 9.87Zm5.42-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47a8.91 8.91 0 0 1-1.65-2.05c-.17-.3-.02-.46.13-.61.13-.13.3-.35.44-.52.15-.17.2-.3.3-.49.1-.2.05-.37-.02-.52-.08-.15-.67-1.61-.91-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.19 1.87.12.57-.08 1.76-.72 2.01-1.41.25-.69.25-1.29.17-1.41-.07-.12-.27-.2-.57-.35Z" />
                  </svg>
                </a>
                <button
                  type="button"
                  className="blog-post-share-btn"
                  onClick={copyLink}
                  aria-label="Linki kopyala"
                  title={linkCopied ? 'Kopyalandı' : 'Linki kopyala'}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.72-1.71" />
                  </svg>
                </button>
                {linkCopied && <span className="blog-post-share-toast">Kopyalandı</span>}
              </div>
            </div>

            <div className="blog-post-stats-row">
              <span className="blog-post-views">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                {formatViews(views)} görüntüleme
              </span>
              {COMMENTS_ENABLED && (
                <a href="#yorumlar" className="blog-post-comments-count">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  {comments.length} yorum
                </a>
              )}
              <button
                type="button"
                className={`blog-post-like-btn${liked ? ' active' : ''}`}
                onClick={toggleLike}
                aria-pressed={liked}
                aria-label={liked ? 'Beğenmekten vazgeç' : 'Beğen'}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill={liked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </button>
            </div>
          </footer>
        </article>

        {/* Son Yazılar — aynı kategoriden 3 ilgili yazı */}
        {relatedPosts.length > 0 && (
          <section className="blog-related-section">
            <div className="blog-related-header">
              <h2 className="blog-related-title">
                “{category}” kategorisindeki diğer yazılar
              </h2>
              <Link
                to={`${ROUTES.blog}?kategori=${encodeURIComponent(category)}`}
                className="blog-related-all"
              >
                Tümünü Gör
              </Link>
            </div>
            <div className="blog-related-grid">
              {relatedPosts.map((rp) => {
                const rpSlug = rp.slug.startsWith('/post/') ? rp.slug : `/post/${rp.slug}`;
                const rpCat = getBlogCategory(rpSlug);
                const rpSeedCount = getSeedComments(rpSlug, rp.title, rpCat).length;
                const rpUserCount = loadComments(rpSlug).length;
                const rpCommentCount = rpSeedCount + rpUserCount;
                const rpLiked = loadLiked(rpSlug);
                return (
                  <Link key={rp.slug} to={rp.slug} className="blog-related-card">
                    <div className="blog-related-image">
                      {rp.image ? (
                        <img src={rp.image} alt={rp.title} loading="lazy" />
                      ) : (
                        <div className="blog-card-placeholder">📝</div>
                      )}
                    </div>
                    <h3 className="blog-related-card-title">{rp.title}</h3>
                    <div className="blog-related-card-meta">
                      <span className="blog-post-views">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                        {formatViews(fakeViewCount(rpSlug, rp.publishedAt))}
                      </span>
                      {COMMENTS_ENABLED && (
                        <span className="blog-post-comments-count">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                          </svg>
                          {rpCommentCount}
                        </span>
                      )}
                      <span className={`blog-related-card-heart${rpLiked ? ' active' : ''}`} aria-hidden="true">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill={rpLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Yorumlar — localStorage tabanlı çalışan sistem */}
        {COMMENTS_ENABLED && (
          <section id="yorumlar" className="blog-comments-section">
            <h3 className="blog-comments-title">Yorumlar</h3>

            <form className="blog-comments-form" onSubmit={submitComment}>
              <input
                className="blog-comments-author-input"
                type="text"
                placeholder="Adınız (isteğe bağlı)"
                value={commentAuthor}
                onChange={(e) => setCommentAuthor(e.target.value)}
                maxLength={60}
              />
              <textarea
                className="blog-comments-input"
                placeholder="Bir yorum yazın..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                rows={3}
                maxLength={2000}
              />
              <div className="blog-comments-form-actions">
                <button
                  type="submit"
                  className="blog-comments-submit"
                  disabled={!commentText.trim()}
                >
                  Gönder
                </button>
              </div>
            </form>

            {comments.length === 0 ? (
              <p className="blog-comments-empty">Henüz yorum yok. İlk yorumu siz yapın.</p>
            ) : (
              <ul className="blog-comments-list">
                {comments.map((c) => (
                  <li key={c.id} className="blog-comment-item">
                    <div className="blog-comment-header">
                      <span className="blog-comment-author">{c.author}</span>
                      <span className="blog-comment-date">{formatCommentDate(c.date)}</span>
                    </div>
                    <p className="blog-comment-text">{c.text}</p>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default BlogPostPage;
